import { logEvent } from '../analytics';
import config from './config';
import { appLocalStorage } from './storage';

const SOURCES = new Set(['content', 'profile', 'in_app_tip', 'in_app_event', 'other']);
const TARGETS = new Set(['opinion', 'story', 'profile', 'app']);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const safeId = value => (typeof value === 'number' || (typeof value === 'string' && /^[1-9]\d*$/.test(value)))
  && Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : undefined;
const currentToken = () => { try { return appLocalStorage.getItem(config.token); } catch { return null; } };

function operationId() {
  try {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch { return null; }
}

// Only a bounded enum and numeric public target id leave the browser. Never
// include a title, user handle, full URL, query string or OS destination name.
export function shareContext(context = {}, address) {
  let inferredType = 'app', inferredId;
  try {
    const path = new URL(address || window.location.href, window.location.href).pathname;
    const match = path.match(/^\/(?:[a-z]{2}\/)?(opinion|news)\/([1-9]\d*)(?:\/|$)/i);
    if (match) {
      inferredType = match[1].toLowerCase() === 'opinion' ? 'opinion' : 'story';
      inferredId = safeId(match[2]);
    } else if (/^\/(?:[a-z]{2}\/)?user\//i.test(path)) inferredType = 'profile';
  } catch { /* A malformed target must never prevent sharing. */ }
  const targetType = TARGETS.has(context?.targetType) ? context.targetType : inferredType;
  const targetId = safeId(context?.targetId) || (targetType === inferredType ? inferredId : undefined);
  const source = SOURCES.has(context?.source) ? context.source
    : targetType === 'profile' ? 'profile' : ['opinion', 'story'].includes(targetType) ? 'content' : 'other';
  const promptId = context?.promptId || context?.notificationId;
  return { source, targetType, ...(targetId ? { targetId } : {}), ...(UUID.test(promptId || '') ? { promptId } : {}) };
}

// One retry only, with the same id/status. The API owns durable idempotency and
// accepts a terminal status before started if requests arrive out of order.
// No persistence/fingerprint is created for guests, and session changes stop
// retries instead of attributing the previous account's action to a new one.
async function send(payload, token) {
  const base = process.env.REACT_APP_API_URL;
  if (!token || !base || typeof fetch !== 'function') return;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (currentToken() !== token) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(`${base.replace(/\/$/, '')}/in-app/share`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', [config.token]: token },
        body: JSON.stringify(payload), signal: controller.signal, cache: 'no-store',
      });
      if (response.ok || (response.status >= 400 && response.status < 500)) return;
    } catch { /* Sharing must never wait for analytics or display its errors. */ }
    finally { clearTimeout(timeout); }
    if (attempt === 0) await new Promise(resolve => setTimeout(resolve, 600));
  }
}

export function createShareTelemetry(context, address) {
  const metadata = shareContext(context, address);
  const id = operationId();
  const token = currentToken();
  let started = false, finished = false;
  const record = status => {
    try { logEvent('ExternalShare', status, `${metadata.source}:${metadata.targetType}`); } catch { /* Analytics is optional. */ }
    if (id && token) void send({ operationId: id, status, ...metadata }, token).catch(() => {});
  };
  const start = () => {
    if (started) return;
    started = true;
    try { window.dispatchEvent(new CustomEvent('wiw:share-initiated', { detail: metadata })); } catch { /* Optional UI integration. */ }
    record('started');
  };
  return {
    start,
    finish(status) {
      if (finished) return;
      finished = true;
      record(status);
    },
  };
}

export function nativeShareOutcome(result) {
  const activity = typeof result?.activityType === 'string' ? result.activityType.trim() : '';
  if (/^com\.apple\.UIKit\.activity\.CopyToPasteboard$/i.test(activity)) return 'copied';
  if (/^com\.apple\.UIKit\.activity\.(?:Message|Mail|AirDrop|PostToFacebook|PostToTwitter|PostToWeibo|PostToTencentWeibo|PostToFlickr|PostToVimeo)$/i.test(activity)) {
    return 'native_handoff';
  }
  // Other Apple tools (print, save, contacts, reading list, markup) and empty
  // results cannot establish external sharing. A third-party extension's
  // bundle identifier establishes handoff, never delivery to a recipient.
  if (!/^com\.apple\./i.test(activity) && /^[a-z][a-z\d_-]*(?:\.[a-z\d_-]+)+$/i.test(activity)) return 'native_handoff';
  return 'native_resolved';
}
